import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonMatchesListComponent } from '@/app/components/shared/skeleton';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { DateLabelComponent } from '@/app/components/shared/date-label/date-label.component';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { isSameDay, isToday } from 'date-fns';
import { MatchApi } from '../../../home/models';
import { MatchByCompetitionItemComponent } from '../../components';
import { CompetitionMatchesPastService } from '../../services';

@Component({
  selector: 'app-results',
  standalone: true,
  imports: [
    NoResultComponent,
    MatchByCompetitionItemComponent,
    SkeletonMatchesListComponent,
    TranslateModule,
    DateLabelComponent,
  ],
  templateUrl: './results.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsComponent {
  private competitionMatchesPastService = inject(CompetitionMatchesPastService);
  private observer!: IntersectionObserver;
  private route = inject(ActivatedRoute);

  competitionId = signal<string>('');

  fetchComputed = computed(() => ({
    isLoading:
      this.competitionMatchesPastService.isLoadingCompetitionsMatchPastFavorites(),
    isError:
      this.competitionMatchesPastService.isErrorCompetitionsMatchPastFavorites(),
  }));
  competitionMatchesPast = computed(() =>
    this.competitionMatchesPastService.competitionsMatchPast()
  );

  scoreContainerRef = viewChild<ElementRef<HTMLDivElement>>(
    'soccerMatchesContainer'
  );
  scrollTriggerRef = viewChild<ElementRef<HTMLDivElement>>('scrollTrigger');

  ngAfterViewInit() {
    // this.performanceService.activate();

    const refItem = this.scrollTriggerRef()?.nativeElement;

    if (!refItem) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            this.competitionMatchesPastService.isMoreMatches() &&
            this.competitionMatchesPastService.competitionsMatchPast() !==
              null &&
            this.competitionMatchesPastService.competitionsMatchPast()!.length >
              9
          ) {
            this.competitionMatchesPastService.getCompetitionMatchesPast(
              this.competitionId(),
              this.competitionMatchesPastService.paginationMatches() + 1
            );
          }
        });
      },
      {
        root: null, // null = viewport
        rootMargin: '200px', // Se activará 200px antes de que el elemento sea visible
        threshold: 0.01,
      }
    );

    this.observer.observe(refItem);

    if (
      !this.competitionMatchesPastService.isLoadingCompetitionsMatchPastFavorites()
    ) {
      this.competitionMatchesPastService.getCompetitionMatchesPast(
        this.competitionId()
      );
    }

    // 👉 Primer fetch con skeleton
    if (
      !this.competitionMatchesPastService.isLoadingCompetitionsMatchPastFavorites()
    ) {
      this.competitionMatchesPastService.getCompetitionMatchesPast(
        this.competitionId()
      );
    }
  }

  constructor() {
    const competitionId = this.route.snapshot.paramMap.get('competitionId');

    if (competitionId) {
      this.competitionId.set(competitionId);
    }
  }

  isNextMatches(date: Date) {
    return !isToday(date);
  }

  isFirstMatchOfDay(matches: MatchApi[], index: number): boolean {
    if (index === 0) return true; // El primer partido siempre muestra la fecha
    const current = matches[index].Date;
    const previous = matches[index - 1].Date;
    return !isSameDay(current, previous);
  }

  ngOnDestroy() {
    this.competitionMatchesPastService.competitionsMatchPast.set(null);
    this.competitionMatchesPastService.competitionsMatchPagination.set(null);
  }
}
